import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Label,
  FormFeedback,
  Button,
  Card,
  CardBody,
  CardTitle,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  ModalFooter,
  Spinner,
  FormGroup,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomerServices from "../../../services/OrderingServices/CustomerServices";
import toast from "react-hot-toast";
import CustomerTypeService from "../../../services/OrderingServices/CustomerTypeService";
import CustomerMetaFieldService from "../../../services/OrderingServices/CustomerMetaFieldService";
import Select from "react-select";

// Validation Schema
const validationSchema = Yup.object({
  // username: Yup.string().required("Username is required"),
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Enter a valid email address"
    )
    .required("Email is required"),
  address: Yup.string().required("Address is required"),
  phoneNo: Yup.string()
    .required("Phone number is required")
    .matches(/^[0-9]+$/, "Phone number must be digits only")
    .min(10, "Phone number must be at least 10 digits")
    .max(10, "Phone number must be exactly 10 digits"),
  customerTypeId: Yup.number().nullable().required("Customer type is required"),
});

const CustomerForm = ({
  selectedCustomer,
  modal,
  toggleModal,
  fetchCustomers,
}) => {
  const [loading, setLoading] = useState(false);
  const [customerTypeData, setCustomerTypeData] = useState([]);
  const [activeMetaFields, setActiveMetaFields] = useState([]);

  const fetchCustomerType = async () => {
    try {
      const response = await CustomerTypeService.getList();
      setCustomerTypeData(response.items || []);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMetaFields = async () => {
    try {
      const response = await CustomerMetaFieldService.getList();
      const items = Array.isArray(response) ? response : response?.items || [];
      setActiveMetaFields(items.filter((field) => field.isActive));
    } catch (error) {
      console.error("Error fetching meta fields:", error);
    }
  };

  useEffect(() => {
    fetchCustomerType();
    fetchMetaFields();
  }, []);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: selectedCustomer?.id || "",

      // username: selectedCustomer?.username || "",
      firstName: selectedCustomer?.firstName || "",
      lastName: selectedCustomer?.lastName || "",
      email: selectedCustomer?.email || "",
      address: selectedCustomer?.address || "",
      phoneNo: selectedCustomer?.phoneNo
        ? String(selectedCustomer.phoneNo)
        : "",
      customerTypeId: selectedCustomer?.customerTypeId || null,
      customerMetaValues: activeMetaFields.map((field) => {
        const existing = selectedCustomer?.customerMetaValues?.find(
          (m) => m.metaFieldCode === field.code,
        );
        return {
          metaFieldCode: field.code,
          value: existing ? existing.value : "",
        };
      }),
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);

        // Prepare payload (ensure only required fields are sent)
        const payload = {
          ...values,
          customerMetaValues: values.customerMetaValues
            .filter((m) => m.value.trim() !== "")
            .map((m) => ({
              metaFieldCode: m.metaFieldCode,
              value: m.value,
            })),
        };

        if (values.id) {
          await CustomerServices.update(values.id, payload);
          toast.success("Customer updated successfully.");
        } else {
          await CustomerServices.create(payload);
          toast.success("New customer is added successfully");
          resetForm();
        }
        fetchCustomers();
        toggleModal();
      } catch (error) {
        console.log(error);
        toast.error(error?.response?.data);
      } finally {
        setLoading(false);
      }
    },
  });
  // Reset form when modal closes
  useEffect(() => {
    if (!modal) {
      formik.resetForm();
    }
  }, [modal]);
  const handleCustomerTypeChange = (selectedOption) => {
    formik.setFieldValue(
      "customerTypeId",
      selectedOption ? selectedOption.value : null
    );
  };

  const customerTypeOptions = customerTypeData.map((type) => ({
    value: type.id,
    label: type.name,
  }));

  const selectedCustomerType = customerTypeOptions.find(
    (option) => option.value === formik.values.customerTypeId
  );



  const handleMetaValueChange = (code, value) => {
    const updated = formik.values.customerMetaValues.map((m) =>
      m.metaFieldCode === code ? { ...m, value } : m,
    );
    formik.setFieldValue("customerMetaValues", updated);
  };

  return (
    <Modal isOpen={modal} toggle={toggleModal} backdrop="static" size="lg">
      <ModalHeader toggle={toggleModal}>
        {selectedCustomer?.id ? "Update" : "Add"} Customer
      </ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Row>
            <Col md={6}>
              <div className="form-floating mb-2">
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  className="form-control"
                  placeholder="Enter first name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.firstName}
                  invalid={
                    !!(formik.touched.firstName && formik.errors.firstName)
                  }
                  style={{ padding: "0.5rem", height: "1rem" }}
                />
                <Label for="firstName">First Name</Label>
                {formik.touched.firstName && formik.errors.firstName && (
                  <FormFeedback type="invalid">
                    {formik.errors.firstName}
                  </FormFeedback>
                )}
              </div>
            </Col>
            <Col md={6}>
              {" "}
              <div className="form-floating mb-2">
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  className="form-control"
                  placeholder="Enter last name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.lastName}
                  invalid={
                    !!(formik.touched.lastName && formik.errors.lastName)
                  }
                  style={{ padding: "0.5rem", height: "1rem" }}
                />
                <Label for="lastName">Last Name</Label>
                {formik.touched.lastName && formik.errors.lastName && (
                  <FormFeedback type="invalid">
                    {formik.errors.lastName}
                  </FormFeedback>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="form-floating mb-2">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  className="form-control"
                  placeholder="Enter email"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  invalid={!!(formik.touched.email && formik.errors.email)}
                  style={{ padding: "0.5rem", height: "1rem" }}
                />
                <Label for="email">Email</Label>
                {formik.touched.email && formik.errors.email && (
                  <FormFeedback type="invalid">
                    {formik.errors.email}
                  </FormFeedback>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="form-floating mb-2">
                <Input
                  id="address"
                  name="address"
                  type="text"
                  className="form-control"
                  placeholder="Enter address"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.address}
                  invalid={!!(formik.touched.address && formik.errors.address)}
                  style={{ padding: "0.5rem", height: "1rem" }}
                />
                <Label for="address">Address</Label>
                {formik.touched.address && formik.errors.address && (
                  <FormFeedback type="invalid">
                    {formik.errors.address}
                  </FormFeedback>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="form-floating mb-2">
                <Input
                  id="phoneNo"
                  name="phoneNo"
                  type="text" // keep as text so value stays string
                  inputMode="numeric" // mobile shows numeric keypad
                  pattern="[0-9]*" // restricts to digits
                  className="form-control"
                  placeholder="Enter phone number"
                  value={formik.values.phoneNo}
                  onChange={(e) => {
                    // Allow only digits
                    const onlyNums = e.target.value.replace(/\D/g, "");
                    formik.setFieldValue("phoneNo", onlyNums);
                  }}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.phoneNo && formik.errors.phoneNo)}
                  style={{ padding: "0.5rem", height: "1rem" }}
                />
                <Label for="phoneNo">Phone Number</Label>
                {formik.touched.phoneNo && formik.errors.phoneNo && (
                  <FormFeedback type="invalid">
                    {formik.errors.phoneNo}
                  </FormFeedback>
                )}
              </div>
            </Col>

            {/* Dynamic Multi-Select Meta Fields */}
            <Col md={12}>
              <hr className="my-2" />
              <FormGroup>
                <Label className="fw-bold mb-3">Customer Meta Fields</Label>
                <Row>
                  {activeMetaFields.map((field) => {
                    const metaValue =
                      formik.values.customerMetaValues.find(
                        (m) => m.metaFieldCode === field.code,
                      )?.value || "";

                    return (
                      <Col md={6} key={field.id} className="mb-3">
                        <Label className="small fw-bold text-muted mb-1">
                          {field.title}
                        </Label>
                        <Input
                          type="text"
                          className="form-control"
                          placeholder={`Enter ${field.title}`}
                          style={{ padding: "0.5rem", height: "1rem" }}
                          value={metaValue}
                          onChange={(e) =>
                            handleMetaValueChange(field.code, e.target.value)
                          }
                        />
                      </Col>
                    );
                  })}
                </Row>
              </FormGroup>
            </Col>

            {/* <Col md={6}>
              <div className="mb-3">
                <Select
                  id="customerTypeId"
                  name="customerTypeId"
                  options={customerTypeOptions}
                  value={selectedCustomerType || null}
                  onChange={handleCustomerTypeChange}
                  placeholder="Select Customer Type"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (baseStyles) => ({
                      ...baseStyles,
                      padding: "0.3rem",
                    }),
                  }}
                />
                {formik.touched.customerTypeId &&
                  formik.errors.customerTypeId && (
                    <div className="text-danger">
                      {formik.errors.customerTypeId}
                    </div>
                  )}
              </div>
            </Col> */}

            <Col md={6}>
              <div className="mb-2">
                <Select
                  id="customerTypeId"
                  name="customerTypeId"
                  options={customerTypeOptions}
                  value={selectedCustomerType || null}
                  onChange={handleCustomerTypeChange}
                  placeholder="Select Customer Type"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor:
                        formik.touched.customerTypeId &&
                        formik.errors.customerTypeId
                          ? "#dc3545" // red border for invalid
                          : baseStyles.borderColor,
                      boxShadow:
                        formik.touched.customerTypeId &&
                        formik.errors.customerTypeId
                          ? "0 0 0 0.2rem rgba(220,53,69,.25)"
                          : baseStyles.boxShadow,
                      padding: "0.3rem",
                    }),
                  }}
                />
                {formik.touched.customerTypeId &&
                  formik.errors.customerTypeId && (
                    <div
                      className="text-danger mt-1"
                      style={{ fontSize: "0.875rem" }}
                    >
                      {formik.errors.customerTypeId}
                    </div>
                  )}
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={toggleModal}>
            Cancel
          </Button>

          <Button color="success" type="submit" disabled={loading}>
            {loading && <Spinner size="sm" className="me-2" />}
            {loading
              ? selectedCustomer?.id
                ? "Updating..."
                : "Creating..."
              : selectedCustomer?.id
              ? "Update"
              : "Create"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CustomerForm;
